/* eslint-disable @typescript-eslint/no-unused-vars */

import {
  Component,
  inject,
  signal,
  ChangeDetectionStrategy,
  OnInit,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormsModule,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  FormControl,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import {
  DialogRef,
  DialogService,
  ButtonComponent,
  InputComponent,
  FieldLabelComponent,
  ConfirmDialogComponent,
  AlertComponent,
} from 'nebula-ui-kit';

import { AppNumericFormatDirective } from '@nebula-shared/util';

export type TipoDeduccion = 'RTF' | 'IVA' | 'ICA' | 'CE' | 'OD';

export interface CuentaDeduccionData {
  codCuenta: string;
  nombreCuenta: string;
  valorBase: number;
  porcentajeDeduccion: number;
  valorDeduccion: number;
  tipoDeduccion?: string;
  baseMinima?: number;
  cuentaId?: number;
}

export interface TerceroInfo {
  id: number;
  numeroDocumento: string;
  nombreCompleto: string;
  autorretenedorRenta?: string;
  granContribuyente?: string;
  regimen?: string;
}

export interface PlanCuentaInfo {
  id: number;
  codigo: string;
  nombre: string;
  tipoDeduccion?: string;
  porcentajeDeduccion?: number;
  baseMinimaDeduccion?: number;
}

function notEmptyValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (value === null || value === undefined || value === '') {
    return { required: true };
  }
  if (typeof value === 'number' && Number.isNaN(value)) {
    return { required: true };
  }
  return null;
}

function numericStringValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const regex = /^-?\d{1,3}(\.\d{3})*(,\d{1,2})?$|^-?\d+(,\d{1,2})?$/;
    if (!regex.test(value)) {
      return { invalidFormat: true };
    }

    return null;
  };
}

function maxDecimalsValidator(decimals: number) {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const strValue = value.toString();
    const decimalPart = strValue.split(',')[1];

    if (decimalPart && decimalPart.length > decimals) {
      return {
        maxDecimals: {
          required: decimals,
          actual: decimalPart.length,
        },
      };
    }

    return null;
  };
}

function nonNegativeValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numValue = parseToNumberStatic(value);
    if (!Number.isNaN(numValue) && numValue < 0) {
      return { negative: true };
    }

    return null;
  };
}

function porcentajeValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (value === null || value === undefined || value === '') {
      return { required: true };
    }
    const numValue = Number(value);
    if (Number.isNaN(numValue)) {
      return { invalidNumber: true };
    }
    if (numValue < 0) {
      return { min: { min: 0, actual: numValue } };
    }
    if (numValue > 100) {
      return { max: { max: 100, actual: numValue } };
    }
    return null;
  };
}

function parseToNumberStatic(formattedValue: any): number {
  if (formattedValue === null || formattedValue === undefined || formattedValue === '') return 0;
  if (typeof formattedValue === 'number' && !Number.isNaN(formattedValue)) return formattedValue;

  let strValue = formattedValue.toString();
  strValue = strValue.replace(/\./g, '');
  strValue = strValue.replace(/,/g, '.');
  strValue = strValue.replace(/[^\d.-]/g, '');

  const numValue = Number.parseFloat(strValue);
  return Number.isNaN(numValue) ? 0 : numValue;
}

@Component({
  selector: 'app-cuenta-deduccion-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    InputComponent,
    FieldLabelComponent,
    AlertComponent,
    AppNumericFormatDirective,
  ],
  templateUrl: './cuenta-deduccion-modal.component.html',
  styleUrls: ['./cuenta-deduccion-modal.component.scss'],
})
export class CuentaDeduccionDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(DialogRef, { optional: true });
  readonly dialogService = inject(DialogService);
  readonly fb = inject(FormBuilder);
  readonly cdr = inject(ChangeDetectorRef);

  @Input() editData?: CuentaDeduccionData;
  @Input() tipoDeduccion?: TipoDeduccion;
  @Input() baseMinima = 0;
  @Input() totalAntesImpuesto = 0;
  @Input() totalImpuesto = 0;
  @Input() terceroInfo?: TerceroInfo;
  @Input() planCuentaInfo?: PlanCuentaInfo;
  @Input() isViewMode = false;

  @Output() saved = new EventEmitter<CuentaDeduccionData>();

  loading = signal(false);
  readonly destroy$ = new Subject<void>();

  mostrarAlerta = signal(false);
  alertaTipo = signal<'success' | 'error' | 'warning' | 'info'>('info');
  alertaTitulo = signal('');
  alertaMensaje = signal('');

  deduccionForm: FormGroup;

  get codCuentaControl(): FormControl {
    return this.deduccionForm.get('codCuenta') as FormControl;
  }

  get nombreCuentaControl(): FormControl {
    return this.deduccionForm.get('nombreCuenta') as FormControl;
  }

  get valorBaseControl(): FormControl {
    return this.deduccionForm.get('valorBase') as FormControl;
  }

  get porcentajeDeduccionControl(): FormControl {
    return this.deduccionForm.get('porcentajeDeduccion') as FormControl;
  }

  get valorDeduccionControl(): FormControl {
    return this.deduccionForm.get('valorDeduccion') as FormControl;
  }

  constructor() {
    this.deduccionForm = this.fb.group({
      codCuenta: ['', [Validators.required, Validators.maxLength(20), notEmptyValidator]],
      nombreCuenta: ['', [Validators.required, Validators.maxLength(100), notEmptyValidator]],
      valorBase: [
        '',
        [
          Validators.required,
          numericStringValidator(),
          maxDecimalsValidator(2),
          nonNegativeValidator(),
        ],
      ],
      porcentajeDeduccion: [null, [Validators.required, porcentajeValidator()]],
      valorDeduccion: [{ value: '0,00', disabled: true }, [Validators.required]],
    });

    this.valorBaseControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calcularDeduccion());

    this.porcentajeDeduccionControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.calcularDeduccion());
  }

  ngOnInit(): void {
    this.configurarValidacionesPorTipo();
    this.configurarPorcentajePorDefecto();

    if (this.editData) {
      this.cargarDatosEdicion();
    } else {
      this.cargarValoresPorDefecto();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Convierte un string con formato español (1.234,56) a número
   */
  parseToNumber(formattedValue: any): number {
    if (formattedValue === null || formattedValue === undefined || formattedValue === '') return 0;

    if (typeof formattedValue === 'number' && !Number.isNaN(formattedValue)) return formattedValue;

    let strValue = formattedValue.toString();

    strValue = strValue.replace(/\./g, '');
    strValue = strValue.replace(/,/g, '.');
    strValue = strValue.replace(/[^\d.-]/g, '');

    const numValue = Number.parseFloat(strValue);
    return Number.isNaN(numValue) ? 0 : numValue;
  }

  /**
   * Formatea un número a formato español (1.234,56)
   */
  formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') return '0,00';

    let numValue: number;

    if (typeof value === 'number') {
      numValue = value;
    } else {
      numValue = this.parseToNumber(value);
    }

    if (isNaN(numValue)) return '0,00';

    return numValue.toLocaleString('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  /**
   * Maneja el evento blur para formatear el valor numérico
   */
  onValueBlur(control: FormControl): void {
    if (control.value && control.value !== '') {
      const numValue = this.parseToNumber(control.value);
      const roundedValue = Math.round(numValue * 100) / 100;
      const formattedValue = this.formatNumber(roundedValue);
      if (control.value !== formattedValue) {
        control.setValue(formattedValue, { emitEvent: true });
        control.updateValueAndValidity();
      }
    }
  }

  /**
   * Configura el porcentaje de deducción desde el plan de cuentas
   */
  private configurarPorcentajePorDefecto(): void {
    if (
      this.planCuentaInfo?.porcentajeDeduccion !== undefined &&
      this.planCuentaInfo.porcentajeDeduccion !== null &&
      this.planCuentaInfo.porcentajeDeduccion > 0
    ) {
      const porcentaje = this.planCuentaInfo.porcentajeDeduccion;

      if (!this.editData || !this.editData.porcentajeDeduccion) {
        this.porcentajeDeduccionControl.setValue(porcentaje);
      }
    }
  }

  private configurarValidacionesPorTipo(): void {
    if (
      this.baseMinima > 0 &&
      (this.tipoDeduccion === 'RTF' ||
        this.tipoDeduccion === 'ICA' ||
        this.tipoDeduccion === 'CE' ||
        this.tipoDeduccion === 'OD')
    ) {
      const currentValidators = this.valorBaseControl.validator;
      this.valorBaseControl.setValidators([
        currentValidators!,
        (control: AbstractControl) => {
          const valorBase = control.value;
          if (valorBase !== null && valorBase !== undefined && valorBase !== '') {
            const valorBaseNum = this.parseToNumber(valorBase);
            if (!Number.isNaN(valorBaseNum) && valorBaseNum < this.baseMinima) {
              return { baseMinima: { required: this.baseMinima, actual: valorBaseNum } };
            }
          }
          return null;
        },
      ]);
      this.valorBaseControl.updateValueAndValidity();
    }
  }

  private cargarValoresPorDefecto(): void {
    let valorBasePorDefecto = 0;
    let porcentajePorDefecto = 0;

    switch (this.tipoDeduccion) {
      case 'RTF':
      case 'ICA':
      case 'CE':
      case 'OD':
        valorBasePorDefecto = this.totalAntesImpuesto;
        porcentajePorDefecto = this.planCuentaInfo?.porcentajeDeduccion || 0;
        break;
      case 'IVA':
        valorBasePorDefecto = this.totalImpuesto;
        porcentajePorDefecto = this.planCuentaInfo?.porcentajeDeduccion || 0;
        break;
    }

    if (valorBasePorDefecto > 0) {
      this.valorBaseControl.setValue(this.formatNumber(valorBasePorDefecto));
    } else {
      this.valorBaseControl.setValue('0,00');
    }

    if (porcentajePorDefecto > 0 && !this.porcentajeDeduccionControl.value) {
      this.porcentajeDeduccionControl.setValue(porcentajePorDefecto);
    } else if (!this.porcentajeDeduccionControl.value) {
      this.porcentajeDeduccionControl.setValue(0);
    }

    if (this.planCuentaInfo) {
      this.codCuentaControl.setValue(this.planCuentaInfo.codigo);
      this.nombreCuentaControl.setValue(this.planCuentaInfo.nombre);

      this.codCuentaControl.disable({ emitEvent: false });
      this.nombreCuentaControl.disable({ emitEvent: false });
    }
  }

  private cargarDatosEdicion(): void {
    if (!this.editData) return;

    this.deduccionForm.patchValue({
      codCuenta: this.editData.codCuenta || '',
      nombreCuenta: this.editData.nombreCuenta || '',
      valorBase: this.formatNumber(this.editData.valorBase),
      porcentajeDeduccion: this.editData.porcentajeDeduccion,
      valorDeduccion: this.formatNumber(this.editData.valorDeduccion),
    });

    this.codCuentaControl.disable({ emitEvent: false });
    this.nombreCuentaControl.disable({ emitEvent: false });
  }

  private calcularDeduccion(): void {
    const valorBaseRaw = this.valorBaseControl.value;
    const porcentaje = Number(this.porcentajeDeduccionControl.value) || 0;

    const valorBase = this.parseToNumber(valorBaseRaw);

    const valorDeduccion = (valorBase * porcentaje) / 100;
    const valorDeduccionRedondeado = Math.round(valorDeduccion * 100) / 100;
    const valorDeduccionFormateado = this.formatNumber(valorDeduccionRedondeado);

    if (this.valorDeduccionControl.value !== valorDeduccionFormateado) {
      this.valorDeduccionControl.setValue(valorDeduccionFormateado, { emitEvent: false });
      this.valorDeduccionControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  getBaseTooltip(): string {
    switch (this.tipoDeduccion) {
      case 'RTF':
        return `Base para retención en la fuente. Total Antes Impuesto: ${this.formatCurrency(this.totalAntesImpuesto)}. Base mínima: ${this.formatCurrency(this.baseMinima)}`;
      case 'IVA':
        return `Base para deducción de IVA. Total Impuesto: ${this.formatCurrency(this.totalImpuesto)}`;
      case 'ICA':
        return `Base para deducción de ICA. Total Antes Impuesto: ${this.formatCurrency(this.totalAntesImpuesto)}`;
      case 'CE':
        return `Base para deducción de Contribuciones Especiales. Total Antes Impuesto: ${this.formatCurrency(this.totalAntesImpuesto)}`;
      case 'OD':
        return `Base para otras deducciones. Total Antes Impuesto: ${this.formatCurrency(this.totalAntesImpuesto)}`;
      default:
        return 'Valor base para calcular la deducción';
    }
  }

  getPorcentajeTooltip(): string {
    if (this.planCuentaInfo?.porcentajeDeduccion) {
      return `Porcentaje de deducción configurado en el plan de cuentas: ${this.planCuentaInfo.porcentajeDeduccion}%. Puede ser modificado manualmente.`;
    }
    return 'Porcentaje de deducción a aplicar (0-100). Puede ser modificado manualmente.';
  }

  getBaseErrorMessage(): string {
    const control = this.valorBaseControl;
    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['maxDecimals'])
      return `Solo se permiten máximo ${control.errors['maxDecimals'].required} decimales`;
    if (control.errors['invalidFormat'])
      return 'Formato inválido. Use punto para miles y coma para decimales (ej: 1.234,56)';
    if (control.errors['negative']) return 'No se permiten valores negativos';
    if (control.errors['baseMinima']) {
      const baseMinimaRequerida = control.errors['baseMinima'].required;
      const valorActual = control.errors['baseMinima'].actual;
      return `El valor base (${this.formatCurrency(valorActual)}) es menor a la base mínima requerida (${this.formatCurrency(baseMinimaRequerida)})`;
    }

    return 'Campo inválido';
  }

  getValorDeduccionErrorMessage(): string {
    const control = this.valorDeduccionControl;
    if (!control || !control.errors || !control.touched) return '';

    const valorDeduccionNum = this.parseToNumber(control.value);
    if (valorDeduccionNum <= 0) {
      return 'El valor de deducción debe ser mayor a cero para poder guardar';
    }

    return '';
  }

  getPorcentajeErrorMessage(): string {
    const control = this.porcentajeDeduccionControl;
    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) return 'El porcentaje de deducción es requerido';
    if (control.errors['min']) return `El porcentaje mínimo es ${control.errors['min'].min}%`;
    if (control.errors['max']) return `El porcentaje máximo es ${control.errors['max'].max}%`;
    if (control.errors['invalidNumber']) return 'Ingrese un número válido';

    return 'Campo inválido';
  }

  getError(fieldName: string): string {
    const control = this.deduccionForm.get(fieldName);
    if (!control || !control.errors || !control.touched) return '';

    if (control.errors['required']) return 'Este campo es requerido';
    if (control.errors['min']) return `El valor mínimo es ${control.errors['min'].min}`;
    if (control.errors['max']) return `El valor máximo es ${control.errors['max'].max}`;
    if (control.errors['maxlength'])
      return `Máximo ${control.errors['maxlength'].requiredLength} caracteres`;

    return 'Campo inválido';
  }

  /**
   * Verifica si el formulario es válido para guardar
   * El botón Guardar se habilita solo si Valor Deducción > 0
   */
  isFormularioValido(): boolean {
    if (this.valorBaseControl.invalid) return false;
    if (this.porcentajeDeduccionControl.invalid) return false;

    const valorDeduccionRaw = this.valorDeduccionControl.value;
    const valorDeduccionNum = this.parseToNumber(valorDeduccionRaw);

    return valorDeduccionNum > 0;
  }

  async onSave(): Promise<void> {
    this.markAllFieldsAsTouched();

    if (!this.isValidForm()) {
      return;
    }

    if (!this.isValidBusinessRules()) {
      return;
    }

    this.loading.set(true);

    try {
      const resultado = this.buildResultData();
      this.mostrarAlertaExito('Cuenta de deducción guardada correctamente');

      setTimeout(() => {
        this.saved.emit(resultado);
        this.dialogRef?.close(resultado);
      }, 500);
    } catch (error) {
      this.mostrarAlertaError('Error al guardar la cuenta de deducción');
    } finally {
      this.loading.set(false);
    }
  }

  private markAllFieldsAsTouched(): void {
    Object.keys(this.deduccionForm.controls).forEach((key) => {
      this.deduccionForm.get(key)?.markAsTouched();
    });
  }

  private isValidForm(): boolean {
    const valorBaseRaw = this.valorBaseControl.value;
    if (this.isEmptyValue(valorBaseRaw)) {
      this.setFieldError(this.valorBaseControl, 'required', 'El valor base es requerido');
      return false;
    }

    const porcentaje = this.porcentajeDeduccionControl.value;
    if (this.isEmptyValue(porcentaje)) {
      this.setFieldError(
        this.porcentajeDeduccionControl,
        'required',
        'El porcentaje de deducción es requerido',
      );
      return false;
    }

    if (this.valorBaseControl.invalid) {
      this.mostrarAlertaError(this.getBaseErrorMessage());
      return false;
    }

    if (this.porcentajeDeduccionControl.invalid) {
      this.mostrarAlertaError(this.getPorcentajeErrorMessage());
      return false;
    }

    const valorDeduccionNum = this.parseToNumber(this.valorDeduccionControl.value);
    if (valorDeduccionNum <= 0) {
      this.mostrarAlertaError('El valor de deducción debe ser mayor a cero');
      return false;
    }

    return true;
  }

  private isValidBusinessRules(): boolean {
    const formValue = this.deduccionForm.getRawValue();
    const valorBaseNum = this.parseToNumber(formValue.valorBase);
    const porcentajeNum = Number(formValue.porcentajeDeduccion);

    if (Number.isNaN(valorBaseNum) || Number.isNaN(porcentajeNum)) {
      this.mostrarAlertaError('Los valores numéricos no son válidos');
      return false;
    }

    if (valorBaseNum <= 0) {
      this.mostrarAlertaError('El valor base debe ser mayor a cero');
      return false;
    }

    if (porcentajeNum < 0 || porcentajeNum > 100) {
      this.mostrarAlertaError('El porcentaje de deducción debe estar entre 0 y 100');
      return false;
    }

    if (this.tipoDeduccion === 'RTF' && this.terceroInfo?.autorretenedorRenta === 'Sí') {
      this.mostrarAlertaError('El tercero es autorretenedor, no aplica retención en la fuente.');
      return false;
    }

    if (this.tipoDeduccion === 'IVA' && this.terceroInfo?.granContribuyente === 'Sí') {
      this.mostrarAlertaError('El tercero es gran contribuyente, no aplica deducción de IVA.');
      return false;
    }

    return true;
  }

  private buildResultData(): CuentaDeduccionData {
    const formValue = this.deduccionForm.getRawValue();
    const valorBaseNum = this.parseToNumber(formValue.valorBase);
    const porcentajeNum = Number(formValue.porcentajeDeduccion);

    const valorDeduccionCalculado = (valorBaseNum * porcentajeNum) / 100;
    const valorDeduccionRedondeado = Math.round(valorDeduccionCalculado * 100) / 100;

    return {
      codCuenta: formValue.codCuenta?.trim() || '',
      nombreCuenta: formValue.nombreCuenta?.trim() || '',
      valorBase: valorBaseNum,
      porcentajeDeduccion: porcentajeNum,
      valorDeduccion: valorDeduccionRedondeado,
      tipoDeduccion: this.tipoDeduccion,
      baseMinima: this.baseMinima,
      cuentaId: this.planCuentaInfo?.id,
    };
  }

  private isEmptyValue(value: any): boolean {
    return value === null || value === undefined || value === '';
  }

  private setFieldError(control: AbstractControl, errorType: string, message: string): void {
    control.setErrors({ [errorType]: true });
    control.markAsTouched();
    this.mostrarAlertaError(message);
  }

  async onCancel(): Promise<void> {
    if (this.deduccionForm.dirty && !this.editData && !this.isViewMode) {
      const confirmed = await this.showConfirmDialog();
      if (!confirmed) {
        return;
      }
    }
    this.dialogRef?.close(null);
  }

  private async showConfirmDialog(): Promise<boolean> {
    const dialogRef = this.dialogService.open(ConfirmDialogComponent, {
      data: {
        title: '¿Cancelar cambios?',
        message: 'Tienes cambios sin guardar. ¿Estás seguro de que deseas cancelar?',
        type: 'warning',
        confirmText: 'Sí, cancelar',
        cancelText: 'No, seguir editando',
      },
      size: 'sm',
    });

    const result = await dialogRef.afterClosed().toPromise();
    return result === true;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('es-CO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value || 0);
  }

  private mostrarAlertaExito(mensaje: string): void {
    this.alertaTipo.set('success');
    this.alertaTitulo.set('Éxito');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);

    setTimeout(() => {
      this.cerrarAlerta();
    }, 3000);
  }

  private mostrarAlertaError(mensaje: string): void {
    this.alertaTipo.set('error');
    this.alertaTitulo.set('Error');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  private mostrarAlertaWarning(mensaje: string): void {
    this.alertaTipo.set('warning');
    this.alertaTitulo.set('Advertencia');
    this.alertaMensaje.set(mensaje);
    this.mostrarAlerta.set(true);
  }

  cerrarAlerta(): void {
    this.mostrarAlerta.set(false);
  }
}
